/**
 * Which surface this document is — and how the answer survives the hop.
 *
 * The embed and the storefront are one build serving one API, so a
 * request carries nothing that says which of the two made it. The
 * funnel needs that distinction (`app/services/embed_funnel.py`), so
 * the client states it in a header.
 *
 * Two values, because the journey has two halves:
 *
 * - ``embed`` — this document IS the embedded configurator. Decided
 *   from the path, not from being framed: an installed app renders our
 *   pages in an iframe too, and the admin's own preview frames
 *   ``/embed`` deliberately.
 * - ``embed-handover`` — the storefront, on a browser that arrived
 *   here from an embed with a cart in hand. The order at the end of
 *   that journey is the embed's last funnel step, and it is placed on
 *   these pages, after the frame is gone.
 *
 * The marker is a hint for a counter and nothing else — the backend
 * says the same in ``app/core/embed_surface.py``. It grants nothing, so
 * there is nothing to gain by forging it and nothing lost when a
 * privacy extension strips it.
 *
 * Enforced by the ``embed-surface-counter-only`` guard
 * (``scripts/repo_checks.py``): ``currentSurface`` and the other
 * "which surface is this" exports may only be imported by
 * ``lib/api.ts``, which attaches the header. ``markEmbedHandover`` /
 * ``clearEmbedHandover`` are lifecycle and stay unrestricted.
 */

/** Header the backend reads. Keep in sync with ``EMBED_SURFACE_HEADER``. */
export const SURFACE_HEADER = "X-Forgmatic-Surface";

const SURFACE_EMBED = "embed";
const SURFACE_HANDOVER = "embed-handover";

const HANDOVER_KEY = "forgmatic.embed-handover";

/** Path the embedded configurator is mounted at (``router.tsx``). */
const EMBED_PATH = "/embed";

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** True when this document is the embedded configurator itself. */
export function isEmbedDocument(): boolean {
  try {
    return window.location.pathname === EMBED_PATH;
  } catch {
    return false;
  }
}

/**
 * Remember that this browser's cart came out of an embed.
 *
 * Called where the handover actually lands — the storefront adopting
 * ``?claim=``. Storing it is what lets the order be attributed at all:
 * by then the frame is gone and nothing else in the request remembers
 * where the visitor came from.
 */
export function markEmbedHandover(): void {
  try {
    storage()?.setItem(HANDOVER_KEY, "1");
  } catch {
    // Quota or a blocked store. The order simply goes uncounted; that
    // is a missing number, not a broken checkout.
  }
}

/**
 * Forget the handover.
 *
 * Called once the order is placed. Without this the marker would
 * outlive the journey it describes and every later order from that
 * browser — including one the visitor started on the shop's own
 * homepage months afterwards — would be credited to the embed.
 */
export function clearEmbedHandover(): void {
  try {
    storage()?.removeItem(HANDOVER_KEY);
  } catch {
    // Nothing to do; a stale marker over-counts, it does not break.
  }
}

export function hasEmbedHandover(): boolean {
  try {
    return storage()?.getItem(HANDOVER_KEY) === "1";
  } catch {
    return false;
  }
}

/** The header value for this request, or null when neither applies. */
export function currentSurface(): string | null {
  if (isEmbedDocument()) return SURFACE_EMBED;
  if (hasEmbedHandover()) return SURFACE_HANDOVER;
  return null;
}
